import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRightOutlined, LinkedinOutlined, XOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { saveIntendedUrl } from '../../utils/persistentNavigation';
import { SEO_DEFAULTS } from '../../utils/seoConfig';
import { generateStorySchema, JsonLd } from '../../utils/structuredData';
import { useInMarketingShell } from '../Marketing/MarketingContext';
import useReveal from '../AIEmployees/useReveal';

import '../AIEmployees/AIEmployeesLandingPage.css';
import './StoryPage.css';

/**
 * StoryPage — "The CellCog Story" at /story. The founder letter, staged like a
 * film: a living cellular-network hero, progressive-illumination paragraphs,
 * and two stat beats — all layered AROUND the letter, never over it. The text
 * is Nitish's letter verbatim; only markup (links/highlights) is added.
 *
 * SEO-first: full letter in semantic HTML (react-snap prerenders it to static
 * HTML for JS-blind crawlers), Article JSON-LD, canonical + OG/Twitter meta.
 * All animation is progressive enhancement — content is fully readable with
 * effects off (prefers-reduced-motion) and during the ReactSnap prerender.
 */

const IS_PRERENDER =
  typeof navigator !== 'undefined' && /ReactSnap/i.test(navigator.userAgent);

/** Reveal-on-scroll wrapper. Forced visible during prerender so the static
 *  snapshot ships every paragraph at full opacity. */
const Reveal = ({ as: Tag = 'div', className = '', children }) => {
  const [ref, visible] = useReveal(0.12);
  const shown = IS_PRERENDER || visible;
  return (
    <Tag ref={ref} className={`story-reveal ${shown ? 'is-visible' : ''} ${className}`}>
      {children}
    </Tag>
  );
};

/** Chapter head — ornamental numeral + a small h2 (design + SEO furniture;
 *  the letter's own words stay untouched below). */
const ChapterHead = ({ num, title }) => (
  <Reveal className="story-ch__head">
    <span className="story-ch__num" aria-hidden="true">{num}</span>
    <h2 className="story-ch__title">{title}</h2>
    <span className="story-ch__rule" aria-hidden="true" />
  </Reveal>
);

const StoryPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isDarkMode } = useTheme();
  // Logged-out marketing surface is DARK-ONLY; logged-in honors the app theme
  // (same pattern as AIEmployeesLandingPage). The hero + close bands stay
  // cinematic dark in BOTH themes via the --aie-hero-* tokens.
  const inMarketingShell = useInMarketingShell();
  const dark = inMarketingShell ? true : isDarkMode;
  const rootRef = useRef(null);
  const barRef = useRef(null);

  const handlePrimary = () => {
    if (currentUser) { navigate('/ai_employee'); return; }
    saveIntendedUrl('/ai_employee');
    navigate('/signup');
  };

  // Reading-progress bar (rAF-throttled). Reads whichever element actually
  // scrolls — window for the marketing shell, the app content pane when
  // logged in (same dual-source pattern as MarketingHeader).
  useEffect(() => {
    if (IS_PRERENDER) return undefined;
    const el = barRef.current;
    if (!el) return undefined;
    const contentEl = document.querySelector('.ant-layout-content.global-main-content');
    let raf = 0;
    const update = () => {
      raf = 0;
      const doc = document.documentElement;
      const y = Math.max(window.scrollY || 0, doc.scrollTop || 0, contentEl ? contentEl.scrollTop : 0);
      const max = Math.max(
        doc.scrollHeight - window.innerHeight,
        contentEl ? contentEl.scrollHeight - contentEl.clientHeight : 0
      );
      const p = max > 0 ? Math.min(1, Math.max(0, y / max)) : 0;
      el.style.transform = `scaleX(${p})`;
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    if (contentEl) contentEl.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (contentEl) contentEl.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Subtle 3D tilt on the stat-beat cards — desktop pointer only, motion-safe.
  useEffect(() => {
    if (IS_PRERENDER) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return undefined;
    const root = rootRef.current;
    if (!root) return undefined;
    const cards = Array.from(root.querySelectorAll('.story-beat__card'));
    const handlers = cards.map((card) => {
      const move = (e) => {
        const r = card.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        card.style.transform = `perspective(900px) rotateY(${(px * 6).toFixed(2)}deg) rotateX(${(py * -6).toFixed(2)}deg) translateY(-2px)`;
      };
      const leave = () => {
        card.style.transform = '';
      };
      card.addEventListener('mousemove', move);
      card.addEventListener('mouseleave', leave);
      return { card, move, leave };
    });
    return () =>
      handlers.forEach(({ card, move, leave }) => {
        card.removeEventListener('mousemove', move);
        card.removeEventListener('mouseleave', leave);
      });
  }, []);

  // Title ≤ ~62 chars and description ≤ ~155 chars so Google shows them
  // untruncated in search results.
  const seo = {
    title: "The CellCog Story — A Founder's Letter on Cellular Multi-Agents",
    description:
      "A founder's letter: how CellCog was built into the AI super-app that reached #1 on DeepResearch Bench (July 2026, and again in October 2026). Today, CellCog runs on its own AI employees.",
  };
  const canonicalUrl = `${SEO_DEFAULTS.url}/story`;
  const ogImage = `${SEO_DEFAULTS.url}/assets/story/og-story.png`;

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" type="text/markdown" href={`${canonicalUrl}/index.md`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />
        <meta property="article:author" content="Nitish Garg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
        <meta name="twitter:image" content={ogImage} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Inter+Tight:wght@500;600;700;800&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <JsonLd data={generateStorySchema()} />

      <div className={`aie-landing story-page ${dark ? 'dark' : 'light'}`} ref={rootRef}>
        <div className="story-progress" ref={barRef} aria-hidden="true" />

        {/* HERO — flat per the adaptive-theme canon (Aug 23: no cells, no
            glows on any marketing surface; the CellNetwork canvas was the
            last survivor of the purge) */}
        <section className="story-hero">
          <div className="story-hero__inner">
            <p className="story-hero__eyebrow">A letter from the founder</p>
            <h1 className="story-hero__title aie-display">The CellCog Story</h1>
            <p className="story-hero__byline">
              <img
                className="story-hero__avatar"
                src="/assets/story/founder-avatar.jpg"
                alt="Nitish Garg, founder of CellCog"
                width="44"
                height="44"
              />
              Nitish Garg &middot; Founder, CellCog
            </p>
            <span className="story-hero__hint" aria-hidden="true">
              Scroll to read
            </span>
          </div>
          <div className="story-hero__fade" aria-hidden="true" />
        </section>

        {/* THE LETTER — verbatim */}
        <article className="story-letter">
          <ChapterHead num="01" title="The moment" />
          <Reveal as="p" className="story-p story-dropcap">
            Like everyone else, I had my moment with ChatGPT. I asked it something
            intricate about infrastructure engineering, and it almost got it right.
            I had never seen the first public version of any technology be that
            good. I knew, right then, that my job was dead &mdash; the whole world
            was about to chase this. That day set off a lifelong obsession:{' '}
            <span className="story-hl">building superintelligence</span>. Not that
            I knew what that meant yet &mdash; at first it was just a raw ambition
            to build the most powerful machines possible using LLMs, somehow. The
            shape of it &mdash; the architecture &mdash; would only reveal itself
            as I kept turning it over.
          </Reveal>

          <ChapterHead num="02" title="The obsession" />
          <Reveal as="p" className="story-p">
            For more than two years, the obsession lived in my head. I knew early
            on I was never going to compete with the big labs on training
            foundation models &mdash; the layer I was imagining sat above theirs.
            What I eventually imagined was a large-scale, self-organizing network
            of billions of agents with access to giga-scale robotics factories
            around the world, where they could build any specialized robot they
            needed. I called it{' '}
            <span className="story-hl">Cellular Multi-Agents</span>.
          </Reveal>
          <Reveal as="p" className="story-p">
            I first tried to get it built inside a large tech company, but it
            never found a home there and took the leap to do this full time.
            Here&rsquo;s the part I don&rsquo;t usually admit: for all that
            thinking, I only ever knew the end goal. The starting point &mdash;
            the path &mdash; revealed itself one step at a time, and only once I
            began building.
          </Reveal>

          <ChapterHead num="03" title="The first cell" />
          <Reveal as="p" className="story-p">
            The first cell I seeded into the network was a coding agent &mdash;
            and its first job was to build CellCog itself: an AI super-app that
            folds every AI capability into one product. Consider what it was up
            against. OpenAI has raised over $190 billion &mdash; $122 billion in
            its latest round alone &mdash; with some of the most brilliant
            researchers alive chasing, in part, the same super-app ambition.
            CellCog shipped one{' '}
            <span className="story-hl">
              bootstrapped, with a CellCog coding agent doing the building
            </span>
            , executing a vision.
            The first cell was alive &mdash; and it was already working.
          </Reveal>

          <Reveal as="aside" className="story-beat" aria-hidden="true">
            <div className="story-beat__card">
              <span className="story-beat__big">$190B+</span>
              <span className="story-beat__label">raised by OpenAI</span>
            </div>
            <span className="story-beat__vs">vs</span>
            <div className="story-beat__card">
              <span className="story-beat__big">$0 raised</span>
              <span className="story-beat__label">and a CellCog coding agent</span>
            </div>
          </Reveal>

          <ChapterHead num="04" title="Number one in the world" />
          <Reveal as="p" className="story-p">
            We then added deep reasoning across text, images, video, and audio
            &mdash; and CellCog hit{' '}
            <a
              className="story-link"
              href="https://huggingface.co/spaces/muset-ai/DeepResearch-Bench-Leaderboard"
              target="_blank"
              rel="noopener noreferrer"
            >
              #1 in the world on the Deep Research Bench
            </a>
            . We&rsquo;ve traded the top spot back and forth with the most heavily
            funded labs all year &mdash; ranging between #1 and #5 as the field
            moves fast &mdash; and as I write this in July 2026, our latest submission is
            verified back at #1.
          </Reveal>

          <Reveal as="aside" className="story-beat story-beat--single" aria-hidden="true">
            <div className="story-beat__card story-beat__card--wide">
              <span className="story-beat__big">#1</span>
              <span className="story-beat__label">
                on DeepResearch Bench (July 2026) &mdash; traded with the most heavily funded
                labs all year
              </span>
            </div>
          </Reveal>

          <Reveal as="p" className="story-p">
            We&rsquo;ve been heads-down building &mdash; no marketing, no launch,
            just word of mouth. This story is the first time I&rsquo;m telling any
            of it publicly. And still, the feedback has been overwhelming. Lawyers
            tell me it&rsquo;s the best AI tool they&rsquo;ve used. Then I heard
            it from finance. Then coding, creative, marketing. People from
            completely different worlds, who have never met each other, keep
            landing on the same phrase:{' '}
            <span className="story-hl">&ldquo;the best agent in the market.&rdquo;</span>{' '}
            I honestly can&rsquo;t use any other AI anymore &mdash; everything
            else feels dumb, even though it runs on the same foundation models.
            Which puts me in an interesting spot: it forces me to build everything
            in-house, on Cellular Multi-Agents.
          </Reveal>

          <Reveal as="p" className="story-p">
            This was always where Cellular Multi-Agents was heading: not tools you
            prompt, but employees you hire. Today, CellCog runs on CellCog. I
            staffed the company with its own AI employees: they coordinate in
            shared channels, delegate work to each other, and some of them manage
            teams of their own. The{' '}
            <Link className="story-link" to="/ai-organization">
              org chart is public
            </Link>
            . It is not a diagram we made for a pitch. It is the live structure of
            this company, updating as it runs.
          </Reveal>

          <ChapterHead num="05" title="The physical body" />
          <Reveal as="p" className="story-p">
            Cellular Multi-Agents are on a great trajectory on the AI side, but I
            think the real payoff comes when the game reaches robotics. Before
            CellCog, I spent most of my life thinking deeply about physical
            systems, and my ideas in robotics are, once again, very different from
            what everyone else is doing. I&rsquo;m waiting for the day I seed the
            network with its first few machines:{' '}
            <span className="story-hl">
              the machines that will build the rest of the physical body of
              Cellular Multi-Agents
            </span>
            .
          </Reveal>
          <Reveal as="p" className="story-p">
            Until then, the digital body is live. Try it at{' '}
            <Link className="story-link" to="/">
              cellcog.ai
            </Link>{' '}
            and judge for yourself. And feel free to reach out at{' '}
            <a className="story-link" href="mailto:nitish@cellcog.ai">
              nitish@cellcog.ai
            </a>{' '}
            if you are curious to learn more about it, I read all the emails.
          </Reveal>

          <Reveal className="story-sig">
            <img
              className="story-sig__ink"
              src="/assets/story/founder-signature.png"
              alt=""
              aria-hidden="true"
              width="188"
              height="79"
              loading="lazy"
            />
            <div className="story-sig__row">
              <img
                className="story-sig__photo"
                src="/assets/story/founder-avatar.jpg"
                alt="Nitish Garg, founder of CellCog"
                width="72"
                height="72"
                loading="lazy"
              />
              <div className="story-sig__id">
                <span className="story-sig__name">Nitish Garg</span>
                <span className="story-sig__role">Founder, CellCog</span>
              </div>
              <div className="story-sig__social">
                <a
                  className="story-sig__social-link"
                  href="https://www.linkedin.com/in/gargnitish/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Nitish Garg on LinkedIn"
                >
                  <LinkedinOutlined />
                </a>
                <a
                  className="story-sig__social-link"
                  href="https://x.com/nitishxgarg"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Nitish Garg on X"
                >
                  <XOutlined />
                </a>
              </div>
            </div>
          </Reveal>
        </article>

        {/* CLOSE — CTA */}
        <section className="story-close">

          <Reveal className="story-close__inner">
            <h2 className="story-close__title aie-display">
              The digital body is live.
            </h2>
            <p className="story-close__sub">Judge it for yourself.</p>
            <button
              type="button"
              className="aie-btn aie-btn-primary story-close__cta"
              onClick={handlePrimary}
            >
              Get started
              <ArrowRightOutlined />
            </button>
          </Reveal>
        </section>
      </div>
    </>
  );
};

export default StoryPage;

